<template>
    <div class="mt-4">
        <div class="bg-white px-4 py-5 shadow sm:rounded-lg sm:px-6">
            <h2 id="timeline-title" class="text-lg font-medium text-gray-900">Pro vývojáře</h2>
            <ul role="list" class="relative z-0 divide-y divide-gray-200 border-gray-200">
                <li class="relative pl-2 pr-3 py-2 sm:py-3 sm:pl-3 lg:pl-4 xl:pl-3">
                    <div class="flex items-center justify-between space-x-4">
                        <!-- Repo name and link -->
                        <div class="min-w-0 space-y-3">
                            <div class="flex items-center space-x-3">
                                <span class="h-4 w-4 bg-green-100 rounded-full flex items-center justify-center" aria-hidden="true">
                                  <span class="h-2 w-2 bg-green-400 rounded-full"></span>
                                </span>

                                <h2 class="text-sm font-medium">
                                    <span>
                                        Soukromý klíč
                                    </span>
                                </h2>
                            </div>
                        </div>
                        <!-- Repo meta info -->
                        <div class="flex flex-col flex-shrink-0 items-end space-y-3">
                            <p class="flex text-gray-500 text-sm space-x-2">
                                <span>Poslední přístup: 1 hodina</span>
                            </p>
                        </div>
                    </div>
                    <p class="mt-2 flex items-center justify-between">
                        <span v-tooltip="'Kliknutím zkopírovat'" @click="copyKey" class="italic text-gray-700 overflow-hidden text-ellipsis whitespace-nowrap">
                            {{ key }}
                        </span>

                        <span class="whitespace-nowrap">
                            <svg v-if="!keyLoaded" @click="showKey" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ml-2 cursor-pointer inline" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                <path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd" />
                            </svg>
                            <svg v-else @click="hideKey" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ml-2 cursor-pointer inline" viewBox="0 0 20 20" fill="currentColor">
                              <path fill-rule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clip-rule="evenodd" />
                              <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                            </svg>
                            <svg @click="rollKey" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ml-2 cursor-pointer inline text-red-500" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
                            </svg>
                        </span>
                    </p>
                </li>

                <li class="relative pl-2 pr-3 py-2 sm:py-3 sm:pl-3 lg:pl-4 xl:pl-3">
                    <div class="flex items-center justify-between space-x-4">
                        <!-- Repo name and link -->
                        <div class="min-w-0 space-y-3">
                            <div class="flex items-center space-x-3">
                                <span class="h-4 w-4 bg-green-100 rounded-full flex items-center justify-center" aria-hidden="true">
                                  <span class="h-2 w-2 bg-green-400 rounded-full"></span>
                                </span>

                                <h2 class="text-sm font-medium">
                                    <span>
                                        Veřejný klíč
                                    </span>
                                </h2>
                            </div>
                        </div>
                    </div>
                    <p class="mt-2 flex items-center justify-between">
                        <span v-tooltip="'Kliknutím zkopírovat'" @click="copyPubkey" class="italic text-gray-700 overflow-hidden text-ellipsis whitespace-nowrap">
                            {{ pubkey }}
                        </span>
                    </p>
                </li>

                <li class="relative pl-2 pr-3 py-3 sm:py-3 sm:pl-3 lg:pl-4 xl:pl-3">
                    <div class="flex items-center justify-between space-x-4">
                        <!-- Repo name and link -->
                        <div class="min-w-0 space-y-3">
                            <div class="flex items-center space-x-3">
                                <span :class="domains.length === 0 ? 'bg-yellow-100' : 'bg-green-100'" class="h-4 w-4 rounded-full flex items-center justify-center" aria-hidden="true">
                                  <span :class="domains.length === 0 ? 'bg-yellow-400' : 'bg-green-400'" class="h-2 w-2 rounded-full"></span>
                                </span>

                                <h2 class="text-sm font-medium">
                                    <span>
                                        Autorizované domény
                                    </span>
                                </h2>
                            </div>
                        </div>
                    </div>
                    <p class="mt-2">
                        <span v-for="(domain, index) in domains">
                            {{ domain.domain }}&nbsp;{{ domain.ip !== domain.domain ? ('('+domain.ip+')') : '(nenalezeno)' }}
                            <svg @click="removeDomain(domain.id)" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 inline text-red-600 cursor-pointer" viewBox="0 0 20 20" fill="currentColor">
                              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clip-rule="evenodd" />
                            </svg>
                            {{ ((index !== domains.length - 1) ? ', ' : '') }}
                        </span>
                        <span v-if="domains.length === 0" class="text-red-600"> (tato bezpečnostní kontrola bude vynechána) </span>
                    </p>
                    <p>
                        <svg @click="addingDomain = true" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-green-600 cursor-pointer" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clip-rule="evenodd" />
                        </svg>
                    </p>
                </li>


                <li class="relative pl-2 pr-3 py-2 sm:py-3 sm:pl-3 lg:pl-4 xl:pl-3">
                    <div class="flex items-center justify-between space-x-4">
                        <!-- Repo name and link -->
                        <div class="min-w-0 space-y-3">
                            <div class="flex items-center space-x-3">
                                <span :class="partnerSettings.webhook_url ? 'bg-green-100' : 'bg-yellow-100' " class="h-4 w-4 rounded-full flex items-center justify-center" aria-hidden="true">
                                  <span :class="partnerSettings.webhook_url ? 'bg-green-400' : 'bg-yellow-400' " class="h-2 w-2 rounded-full"></span>
                                </span>

                                <h2 class="text-sm font-medium">
                                    <span>
                                        Webhook URL (bez předpony protokolu)
                                    </span>
                                </h2>
                            </div>
                        </div>
                    </div>
                    <p class="mt-2 flex items-center justify-between">
                        <span v-if="!newWebhookUrl.open" class="italic text-gray-700 overflow-hidden text-ellipsis whitespace-nowrap">
                            {{ partnerSettings.webhook_url ?? '--nenastaveno--' }}
                        </span>
                        <span v-else class="w-full mr-2">
                            <jet-input required id="name" type="text" class="mt-1 block w-full" v-model="newWebhookUrl.webhook_url" />
                            <jet-input-error :message="newWebhookUrl.errors.webhook_url" class="mt-2" />
                        </span>

                        <span class="whitespace-nowrap">
                            <svg v-if="!newWebhookUrl.open" @click="newWebhookUrl.open = true" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 cursor-pointer" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                            </svg>
                            <svg v-if="newWebhookUrl.open" @click="saveWebhookUrl" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-green-600 cursor-pointer inline" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                            </svg>
                            <svg v-if="newWebhookUrl.open" @click="resetWebhookUrl" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-red-500 cursor-pointer inline ml-2" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                            </svg>
                        </span>
                    </p>
                </li>

                <!-- More projects... -->
            </ul>
        </div>
    </div>

    <dialog-modal :show="addingDomain">
        <template #icon>
            <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                <!-- Heroicon name: exclamation -->
                <svg class="h-6 w-6 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
            </div>
        </template>
        <template #title>Přidání autorizované domény</template>
        <template #content>
            <!-- Name -->
            <div class="mt-2 col-span-6 sm:col-span-4">
                <jet-label required for="name">Doména</jet-label>
                <jet-input required id="name" type="text" class="mt-1 block w-full" v-model="newDomain.domain" />
                <jet-input-error :message="newDomain.errors.domain" class="mt-2" />
            </div>

        </template>
        <template #footer>
            <jet-button :class="{ 'opacity-25': newDomain.processing }" :disabled="newDomain.processing" @click.native="addDomain" class="ml-2">
                Přidat
            </jet-button>
            <secondary-button :class="{ 'opacity-25': newDomain.processing }" :disabled="newDomain.processing" @click.native="stopAddingDomain">
                Zrušit
            </secondary-button>
        </template>
    </dialog-modal>
</template>

<script>
import {useToast} from "vue-toastification";
import DialogModal from "../../Jetstream/DialogModal";
import SecondaryButton from "../../Jetstream/SecondaryButton";
import Button from "../../Jetstream/Button";
import Label from "../../Jetstream/Label";
import InputError from "../../Jetstream/InputError";
import JetInput from "../../Jetstream/Input";

export default {
    name: "Dev",
    components: {InputError, Label, SecondaryButton, DialogModal, 'jet-button': Button, 'jet-label': Label, JetInput, 'jet-input-error': InputError},
    data(){
        return {
            keyLoaded: false,
            partnerSettings: this.$page.props.partnerSettings,
            key: '******************************',
            pubkey: this.$page.props.pubkey,
            domains: this.$page.props.domains,
            addingDomain: false,
            newDomain: this.$inertia.form({
                domain: null,
            }),
            newWebhookUrl: this.$inertia.form({
                open: false,
                name: this.$page.props.partnerSettings.name,
                logo: null,
                webhook_url: this.$page.props.partnerSettings.webhook_url,
            }),
        }
    },

    setup(){
        const toast = useToast();
        return { toast }
    },

    methods: {
        saveWebhookUrl(){
            this.newWebhookUrl.post(route('partner.settings'), { preserveState: false })
        },
        resetWebhookUrl(){
            this.newWebhookUrl.reset();
        },
        addDomain(){
            this.newDomain.post(route('partner.domain'), { preserveState: false,} );
        },
        stopAddingDomain(){
            this.newDomain.reset();
            this.addingDomain = false;
        },
        removeDomain(id){
            this.$inertia.delete(route('partner.domain.delete', {id: id}), {preserveState: false});
        },
        showKey() {
            axios.get(route('partner.key'))
                .then( (response) => {
                    this.key = response.data.key;
                    this.keyLoaded = true;
                    setTimeout(this.hideKey, 15000)
                });
        },
        rollKey(){
            axios.delete(route('partner.key.roll'))
                .then( (response) => {
                    this.key = response.data.key;
                    this.pubkey = response.data.pubkey;
                    setTimeout(this.hideKey, 15000);
                    this.toast.success('Klíč zneplatněn');
                });
        },
        hideKey(){
            this.keyLoaded = false;
            this.key = '******************************';
        },
        copyKey(){
            if(this.keyLoaded) navigator.clipboard.writeText(this.key);
            else {
                axios.get(route('partner.key'))
                    .then( (response) => {
                        navigator.clipboard.writeText(response.data.key);
                    });
            }
            this.toast.success('Zkopírováno')
        },
        copyPubkey(){
            navigator.clipboard.writeText(this.pubkey);
            this.toast.success('Zkopírováno')
        }
    }
}
</script>

<style scoped>

</style>
